import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Typography, Box, CircularProgress, Container, Breadcrumbs, Link, Button, IconButton, Rating, TextField, Snackbar, Alert, Chip, useMediaQuery, useTheme } from "@mui/material";
import { Table, TableBody, TableCell, TableContainer, TableRow, Paper, Tabs, Tab, Grid, Modal } from '@mui/material';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ArrowBackIos, ArrowForwardIos, Close } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import Notes from './header';
import FooterManual from './footerManual';
import "./productPage.css";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addToCart, fetchCart } from './store';
import { useSnackbar } from 'notistack';
import { Favorite, FavoriteBorder } from "@mui/icons-material";
import { addToWishlist, fetchWishlist, removeFromWishlist } from './store';
import AffordableCarSection from './affordableCarSection';
import InquiryModal from './inquiryModal';
import ProductInquiry from './productInquiry';
import ProductPurchaseFlow from './productPurchaseFlow';

// ==================== DEVICE DETECTION HOOKS ====================
const useDeviceType = (theme) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 960px
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px
  
  return { isMobile, isTablet, isDesktop };
};

// ==================== RESPONSIVE STYLES GENERATOR ====================
const getResponsiveStyles = (isMobile, isTablet, isDesktop) => ({
  globalWrapper: {
    width: '100%',
    maxWidth: '100%',
    padding: 0,
    margin: 0,
    overflow: 'hidden',
  },

  mainContainer: {
    width: '100%',
    maxWidth: '100%',
    margin: '0 auto',
    padding: isMobile ? '12px' : isTablet ? '20px' : '24px 32px',
  },

  breadcrumbs: {
    marginBottom: isMobile ? '16px' : isTablet ? '24px' : '32px',
    paddingTop: isMobile ? '8px' : '16px',
    fontSize: isMobile ? '0.75rem' : 'inherit',
  },

  productLayout: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? '20px' : isTablet ? '40px' : '80px',
    marginBottom: isMobile ? '40px' : isTablet ? '48px' : '64px',
    width: '100%',
  },

  imageContainer: {
    flex: isMobile ? '1 1 100%' : isTablet ? '1 1 45%' : '1 1 35%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    borderRadius: '12px',
  },

  mainImageContainer: {
    position: 'relative',
    width: '100%',
    height: isMobile ? '300px' : isTablet ? '400px' : '500px',
    overflow: 'hidden',
    borderRadius: '8px',
    cursor: 'pointer',
    backgroundColor: '#f9f9f9',
  },

  mainImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    cursor: 'pointer',
  },

  navButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    padding: isMobile ? '6px' : '8px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    zIndex: 2,
    minWidth: 'auto',
    minHeight: 'auto',
  },

  leftButton: {
    left: isMobile ? '6px' : '10px',
  },

  rightButton: {
    right: isMobile ? '6px' : '10px',
  },

  imageTypeSelector: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: isMobile ? '4px' : '8px',
    marginBottom: '12px',
    marginTop: '12px',
    flexWrap: 'wrap',
  },

  imageTypeButton: {
    padding: isMobile ? '6px 8px' : '8px 16px',
    minWidth: isMobile ? '60px' : '120px',
    borderRadius: '4px',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    flex: isMobile ? '1' : 'auto',
    fontSize: isMobile ? '0.65rem' : '0.875rem',
    height: isMobile ? '36px' : '40px',
  },

  imageCounter: {
    position: 'absolute',
    bottom: '10px',
    right: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: isMobile ? '12px' : '14px',
  },

  productInfo: {
    flex: isMobile ? '1 1 100%' : isTablet ? '1 1 55%' : '1 1 65%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: '0',
  },

  ratingBar: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
    gap: '8px',
  },

  priceContainer: {
    marginTop: isMobile ? '20px' : isTablet ? '24px' : '36px',
    marginBottom: isMobile ? '20px' : isTablet ? '24px' : '36px',
  },

  quantityControls: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: isMobile ? '24px' : '40px',
    gap: isMobile ? '8px' : '12px',
  },

  buttons: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? '12px' : isTablet ? '16px' : '32px',
    width: '100%',
  },

  actionButton: {
    height: isMobile ? '44px' : '54px',
    flex: isMobile ? '1' : 'auto',
    width: isMobile ? '100%' : 'auto',
    minWidth: isMobile ? 'auto' : '200px',
    fontSize: isMobile ? '0.875rem' : '1rem',
  },

  specsTable: {
    width: '100%',
    margin: isMobile ? '32px 0' : isTablet ? '48px auto' : '64px auto',
    marginBottom: isMobile ? '24px' : '40px',
    padding: 0,
  },

  tableContainer: {
    width: '100%',
    borderRadius: '12px',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
  },

  tabContent: {
    padding: isMobile ? '16px' : isTablet ? '24px' : '40px',
  },

  secondaryImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: isMobile ? '250px' : isTablet ? '350px' : '450px',
    overflow: 'hidden',
    borderRadius: '8px',
    marginTop: isMobile ? '24px' : isTablet ? '32px' : '48px',
    marginBottom: isMobile ? '24px' : isTablet ? '32px' : '48px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
    backgroundColor: '#f9f9f9',
  },

  secondaryImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '8px',
  },

  sectionHeading: {
    marginBottom: isMobile ? '16px' : isTablet ? '24px' : '32px',
    color: '#f44336',
    fontSize: isMobile ? '1.5rem' : isTablet ? '2rem' : '2.5rem',
  },

  infoSection: {
    marginBottom: isMobile ? '24px' : isTablet ? '32px' : '48px',
  },

  tabContainer: {
    width: '100%',
    marginTop: isMobile ? '20px' : '40px',
    marginBottom: isMobile ? '32px' : '64px',
  },

  titleText: {
    fontWeight: 600,
  },

  imageModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  modalContent: {
    position: 'relative',
    maxWidth: isMobile ? '95vw' : '90vw',
    maxHeight: '90vh',
    borderRadius: '8px',
    overflow: 'hidden',
  },

  modalImage: {
    width: '100%',
    maxHeight: '85vh',
    objectFit: 'contain',
  },

  modalCloseButton: {
    position: 'absolute',
    top: isMobile ? '8px' : '16px',
    right: isMobile ? '8px' : '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    color: '#000',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
    },
    zIndex: 3,
    minWidth: 'auto',
    minHeight: 'auto',
  },

  modalNavButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    color: '#000',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
    },
    zIndex: 3,
    minWidth: 'auto',
    minHeight: 'auto',
  },

  modalLeftButton: {
    left: isMobile ? '8px' : '16px',
  },

  modalRightButton: {
    right: isMobile ? '8px' : '16px',
  },

  modalButtonsContainer: {
    position: 'absolute',
    top: isMobile ? '10px' : '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: isMobile ? '6px' : '16px',
    zIndex: 4,
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: isMobile ? '90%' : 'auto',
  },

  modalTypeButton: {
    padding: isMobile ? '6px 10px' : '8px 16px',
    minWidth: isMobile ? '70px' : '120px',
    borderRadius: '4px',
    fontWeight: 'bold',
    backgroundColor: 'rgba(62, 98, 89, 0.8)',
    color: '#ffffff',
    textTransform: 'none',
    fontSize: isMobile ? '0.65rem' : '0.875rem',
    '&:hover': {
      backgroundColor: 'rgba(44, 71, 63, 0.9)',
    },
  },

  activeModalTypeButton: {
    backgroundColor: 'rgba(255, 87, 34, 0.9)',
    '&:hover': {
      backgroundColor: 'rgba(230, 74, 25, 0.9)',
    },
  },

  thumbnailGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(40px, 1fr))' : 'repeat(auto-fill, minmax(50px, 1fr))',
    gap: isMobile ? '6px' : '8px',
    marginTop: '12px',
  },

  warningBox: {
    mt: isMobile ? 1 : 2,
    p: isMobile ? 1 : 2,
  },

  warningText: {
    fontSize: isMobile ? '0.75rem' : '0.875rem',
    mb: isMobile ? 0.5 : 1,
  },

  chipContainer: {
    marginTop: isMobile ? '12px' : '15px',
    bottom: 8,
    left: 8,
    display: 'flex',
    gap: isMobile ? '6px' : '12px',
    flexWrap: 'wrap',
  },

  chipSize: {
    fontSize: isMobile ? '0.55rem' : '0.65rem',
    height: isMobile ? '20px' : '26px',
  },
});

// ==================== MAIN COMPONENT ====================
const ProductPage = ({ openCartDrawer }) => {
  const { modelId } = useParams();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { isMobile, isTablet, isDesktop } = useDeviceType(theme);
  
  const [tabValue, setTabValue] = useState(0);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [imageError, setImageError] = useState({});
  const [added, setAdded] = useState(false);
  const [open, setOpen] = useState(false);
  const [isInCart, setIsInCart] = useState(false);
  const cartItems = useSelector((state) => state.cart.items);
  const navigate = useNavigate();
  const [cartLoading, setCartLoading] = useState(true);
  const wishlistItems = useSelector((state) => state.wishlist.items);
  const [isFavorite, setIsFavorite] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [accessoriesData, setAccessoriesData] = useState({
    moreAccessories: [],
    filterAccessories: []
  });
  const [productMoreAccessoryIds, setProductMoreAccessoryIds] = useState(new Set());
  const [productFilterAccessoryIds, setProductFilterAccessoryIds] = useState(new Set());
  const [currentImageType, setCurrentImageType] = useState("exterior");
  
  // Enhanced image gallery states
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [exteriorImages, setExteriorImages] = useState([]);
  const [interiorImages, setInteriorImages] = useState([]);
  const [interiorLoaded, setInteriorLoaded] = useState(false);
  const [detailsImageError, setDetailsImageError] = useState(false);
  const cartState = useSelector(state => state.cart);

  const styles = getResponsiveStyles(isMobile, isTablet, isDesktop);

  // ==================== IMAGE UTILITIES ====================
  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const generateValidImageUrls = async (type, modelId, maxCount = 15) => {
    const baseUrl = `https://car.amar.co.jp/api/image/car-${type}-images-`;

    const urls = await Promise.allSettled(
      Array.from({ length: maxCount }, (_, i) =>
        checkImageExists(`${baseUrl}${i + 1}/get-image/${modelId}`)
      )
    );

    return urls
      .filter(result => result.status === 'fulfilled' && result.value)
      .map(result => result.value);
  };

  const detailsImage = `https://car.amar.co.jp/api/image/car-exterior-images-16/get-image/${modelId}`;

  // ==================== ACCESSORIES FETCH ====================
  const fetchAccessories = async () => {
    try {
      const res = await axios.get("https://car.amar.co.jp/api/dashboard/product/get-all-accessories");

      console.log("Accessories API response:", res.data);

      if (Array.isArray(res.data)) {
        setAccessoriesData({
          moreAccessories: res.data,
          filterAccessories: []
        });
      } else if (res.data && typeof res.data === 'object') {
        setAccessoriesData({
          moreAccessories: Array.isArray(res.data.moreAccessories) ? res.data.moreAccessories : [],
          filterAccessories: Array.isArray(res.data.filterAccessories) ? res.data.filterAccessories : []
        });
      } else {
        setAccessoriesData({ moreAccessories: [], filterAccessories: [] });
      }
    } catch (err) {
      console.error("Failed to fetch accessories:", err);
      setAccessoriesData({ moreAccessories: [], filterAccessories: [] });
    }
  };

  // ==================== EFFECTS ====================
  useEffect(() => {
    fetchAccessories();
  }, []);

  useEffect(() => {
    console.log("Current accessoriesData:", accessoriesData);
    console.log("moreAccessories:", accessoriesData.moreAccessories);
    console.log("filterAccessories:", accessoriesData.filterAccessories);
    console.log("Product accessories:", product?.moreAccessories, product?.filterAccessories);
  }, [accessoriesData, product]);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      const fetchData = async () => {
        try {
          setCartLoading(true);
          await dispatch(fetchCart());
          await dispatch(fetchWishlist());
        } catch (err) {
          console.error('Error fetching cart/wishlist:', err);
        } finally {
          setCartLoading(false);
        }
      };
      fetchData();
    } else {
      setCartLoading(false);
    }
  }, [modelId, dispatch]);

  useEffect(() => {
    if (cartItems && modelId) {
      const itemInCart = cartItems.some(item => item.modelId.toString() === modelId.toString());
      setIsInCart(itemInCart);
    }
  }, [cartItems, modelId]);

  useEffect(() => {
    if (product && wishlistItems) {
      const isInWishlist = wishlistItems.some(item => item.modelId.toString() === modelId.toString());
      setIsFavorite(isInWishlist);
    }
  }, [product, wishlistItems, modelId]);

  useEffect(() => {
    if (!modelId) return;

    const loadExteriorImages = async () => {
      const exteriors = await generateValidImageUrls("exterior", modelId);
      setExteriorImages(exteriors);
    };

    loadExteriorImages();
  }, [modelId]);

  // Reset image carousel to the first image, and back to Exterior tab, whenever the product changes
  useEffect(() => {
    setCurrentImageIndex(0);
    setCurrentImageType("exterior");
    setInteriorLoaded(false);
  }, [modelId]);

  const loadInteriorImages = async () => {
    if (!interiorLoaded) {
      const interiors = await generateValidImageUrls("interior", modelId);
      setInteriorImages(interiors);
      setInteriorLoaded(true);
    }
  };

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);

        if (!modelId) {
          setError('Model ID is required');
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `https://car.amar.co.jp/api/dashboard/product/get/Model/details/${modelId}`,
          {
            headers: {
              'Accept': 'application/json',
              'Accept-Language': 'en'
            }
          }
        );

        setProduct(response.data.data);
        const productMoreAccessoryIds = new Set(response.data.data.moreAccessories?.map(acc => acc.carMoreAccessoriesId));
        const productFilterAccessoryIds = new Set(response.data.data.filterAccessories?.map(acc => acc.carFilterAccessoriesId));
        setProductMoreAccessoryIds(productMoreAccessoryIds);
        setProductFilterAccessoryIds(productFilterAccessoryIds);

      } catch (err) {
        setError('Failed to fetch product data');
        console.error('Error fetching product data:', err);

        if (err.response) {
          console.error('Response status:', err.response.status);
          console.error('Response data:', err.response.data);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [modelId]);

  // ==================== IMAGE NAVIGATION ====================
  const currentImages =
    currentImageType === "exterior"
      ? exteriorImages
      : currentImageType === "interior"
        ? interiorImages
        : [detailsImage];

  const handleNextImage = () => {
    if (currentImageIndex === currentImages.length - 1) {
      if (currentImageType === "exterior") {
        loadInteriorImages();
        if (interiorImages.length > 0) {
          setCurrentImageType("interior");
          setCurrentImageIndex(0);
        }
      } else if (currentImageType === "interior") {
        setCurrentImageType("exterior");
        setCurrentImageIndex(0);
      }
    } else {
      setCurrentImageIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handlePrevImage = () => {
    if (currentImageIndex === 0) {
      if (currentImageType === "exterior") {
        loadInteriorImages();
        setTimeout(() => {
          setCurrentImageType("interior");
          setCurrentImageIndex(Math.max(0, interiorImages.length - 1));
        }, 100);
      } else if (currentImageType === "interior") {
        setCurrentImageType("exterior");
        setCurrentImageIndex(Math.max(0, exteriorImages.length - 1));
      }
    } else {
      setCurrentImageIndex((prevIndex) => prevIndex - 1);
    }
  };

  const handleModalNextImage = () => {
    if (modalImageIndex === currentImages.length - 1) {
      if (currentImageType === "exterior") {
        loadInteriorImages();
        if (interiorImages.length > 0) {
          setCurrentImageType("interior");
          setModalImageIndex(0);
        }
      } else if (currentImageType === "interior") {
        setCurrentImageType("exterior");
        setModalImageIndex(0);
      }
    } else {
      setModalImageIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handleModalPrevImage = () => {
    if (modalImageIndex === 0) {
      if (currentImageType === "exterior") {
        setCurrentImageType("interior");
        setModalImageIndex(interiorImages.length - 1);
      } else if (currentImageType === "interior") {
        setCurrentImageType("exterior");
        setModalImageIndex(exteriorImages.length - 1);
      }
    } else {
      setModalImageIndex((prevIndex) => prevIndex - 1);
    }
  };

  const switchImageType = (type) => {
    if (type === 'interior' && !interiorLoaded) {
      loadInteriorImages();
    }
    setCurrentImageType(type);
    setCurrentImageIndex(0);
    setModalImageIndex(0);
  };

  const openImageModal = (index) => {
    setModalImageIndex(index);
    setModalOpen(true);
  };

  const handleImageError = (index) => {
    setImageError(prev => ({
      ...prev,
      [currentImageType + index]: true
    }));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleQuantityChange = (e) => {
    setQuantity(parseInt(e.target.value) || 1);
  };

  const incrementQuantity = () => {
    setQuantity(prev => prev + 1);
  };

  const decrementQuantity = () => {
    setQuantity(prev => (prev > 1 ? prev - 1 : 1));
  };

  // ==================== CART HANDLERS ====================
  const handleAddToCart = async (modelId) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      setCartLoading(true);

      if (isInCart) {
        enqueueSnackbar("Item is already in your cart", { variant: "warning" });
        setCartLoading(false);
        return;
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-cart-and-whishlist/manage/item?modelId=${modelId}&SelectType=ADD_IN_CART`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            modelId: modelId,
            type: "ADD_IN_CART",
          }),
        }
      );
      await dispatch(fetchCart());
      const data = await response.json();
      if (data.code === 200) {
        enqueueSnackbar("Added to cart successfully", { variant: "success" });
        await dispatch(fetchCart());
        setIsInCart(true);
      } else {
        enqueueSnackbar(data.message || "Failed to add to cart", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error adding to cart", { variant: "error" });
      console.error("Error adding to cart:", error);
    } finally {
      setCartLoading(false);
    }
  };

  // ==================== WISHLIST HANDLERS ====================
  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      if (isFavorite) {
        setOpenConfirmDialog(true);
      } else {
        const result = await dispatch(addToWishlist(modelId));
        if (result.success) {
          setIsFavorite(true);
          enqueueSnackbar("Added to wishlist", { variant: "success" });
        } else {
          enqueueSnackbar(result.message || "Failed to add to wishlist", {
            variant: "error",
          });
        }
        dispatch(fetchWishlist());
      }
    } catch (error) {
      enqueueSnackbar("Error updating wishlist", { variant: "error" });
      console.error("Error updating wishlist:", error);
    }
  };

  // ==================== DOWNLOAD HANDLER ====================
  const handleDownloadAll = async () => {
    try {
      enqueueSnackbar("Preparing download...", { variant: "info" });

      const downloadUrl = `https://car.amar.co.jp/api/image/api/aucnet/car-images/download/${modelId}`;

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${product.brandName}_${product.modelName}_images.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      enqueueSnackbar("Download started!", { variant: "success" });
    } catch (error) {
      console.error("Download failed:", error);
      enqueueSnackbar("Download failed. Please try again.", { variant: "error" });
    }
  };

  // ==================== UTILITY FUNCTIONS ====================
  const formatCurrency = (price, currency) => {
    return `${currency} ${price.toLocaleString()}`;
  };

  const formatValue = (value, suffix = "") => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    return suffix ? `${value} ${suffix}` : value;
  };

  // ==================== LOADING & ERROR STATES ====================
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // ==================== IMAGE URLS ====================
  const imageUrl1 = exteriorImages[0];
  const imageUrl2 = exteriorImages[1];
  const imageUrl3 = exteriorImages[2];

  // ==================== TAB CONTENT RENDERER ====================
  const renderTabContent = (index) => (
    <>
      {index === 0 && (
        <Grid container spacing={isMobile ? 3 : 6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography
              variant="h5"
              component="h1"
              color="#f44336"
              gutterBottom
              sx={{ fontWeight: 600, fontSize: isMobile ? '1.25rem' : '1.5rem' }}
            >
              {product?.brandName} {product?.modelName}
            </Typography>
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ mb: 4, fontSize: isMobile ? '1rem' : '1.25rem' }}
            >
              {product?.categoriesName} - {product?.year}
            </Typography>

            <Typography
              paragraph
              sx={{ mb: 3, lineHeight: 1.8, fontSize: isMobile ? "14px" : "16px" }}
            >
              Automobiles, which play an important role in meeting the needs of
              transportation and cargo transportation in daily life, should not
              be neglected for cleaning and maintenance. In addition, the
              aesthetic stance and appearance of the cars are at least as
              important as their cleaning and maintenance.
            </Typography>

            <Typography paragraph sx={{ lineHeight: 1.8, fontSize: isMobile ? "14px" : "16px" }}>
              The {product?.brandName} {product?.modelName} {product?.year}{" "}
              features a {product?.carEngineCC} engine with{" "}
              {product?.carTransmission} transmission. Its fuel efficiency is
              rated at {product?.carMileage}, making it an excellent choice for
              your transportation needs.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <img
                src={imageUrl2}
                alt={`${product.brandName} ${product.modelName}`}
                style={{ width: "100%", borderRadius: "8px" }}
                onError={(e) => {
                  if (!imageError) {
                    setImageError(true);
                    e.target.src = "carousel/carslider.png";
                  }
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <img
                src={imageUrl3}
                alt={`${product.brandName} ${product.modelName}`}
                style={{ width: "100%", borderRadius: "8px" }}
                onError={(e) => {
                  if (!imageError) {
                    setImageError(true);
                    e.target.src = "carousel/carslider.png";
                  }
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="h5"
              component="h1"
              color="#f44336"
              gutterBottom
              sx={{ fontWeight: 600, fontSize: isMobile ? '1.25rem' : '1.5rem' }}
            >
              Features and Benefits
            </Typography>
            <Typography 
              variant="h6" 
              component="h2" 
              gutterBottom 
              sx={{ mb: 4, fontSize: isMobile ? '1rem' : '1.25rem' }}
            >
              {product?.brandName} Quality
            </Typography>

            <Typography
              paragraph
              sx={{ mb: 3, lineHeight: 1.8, fontSize: isMobile ? "14px" : "16px" }}
            >
              The {product?.modelName} from {product?.brandName} showcases the
              latest in automotive technology and design. With its sleek lines
              and powerful {product?.carEngineCC} engine, it stands out in the{" "}
              {product?.categoriesName} category.
            </Typography>

            <Typography paragraph sx={{ lineHeight: 1.8, fontSize: isMobile ? "14px" : "16px" }}>
              Priced at {product?.carPrice} {product?.carPriceCurrency}, this
              vehicle represents excellent value for money, combining
              performance, comfort, and reliability in a single package.
            </Typography>
          </Grid>
        </Grid>
      )}
    </>
  );

  return (
    <>
      <Notes />
      <Container sx={styles.globalWrapper} disableGutters maxWidth={false}>
        <Box sx={styles.mainContainer}>
          <Breadcrumbs aria-label="breadcrumb" sx={styles.breadcrumbs}>
            <Link underline="hover" color="inherit" href="#">Home</Link>
            <Link underline="hover" color="inherit" href="#">All Brands</Link>
            <Link underline="hover" color="inherit" href="#">{product.brandName}</Link>
            <Typography color="text.primary">{product.modelName}</Typography>
          </Breadcrumbs>

          <Box sx={styles.productLayout}>
            {/* Enhanced Image Gallery */}
            <Box sx={styles.imageContainer}>
              {/* Type Toggle Buttons */}

              {/* Main Image */}
              <Box sx={styles.mainImageContainer}>
                <IconButton
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "rgba(255,255,255,0.9)",
                    padding: "8px",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,1)",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s ease",
                    zIndex: 2,
                  }}
                  onClick={handleFavoriteClick}
                >
                  {isFavorite ? (
                    <Favorite sx={{ color: "red", fontSize: "1.25rem" }} />
                  ) : (
                    <FavoriteBorder sx={{ fontSize: "1.25rem", color: "grey.600" }} />
                  )}
                </IconButton>

                <img
                  src={currentImages[currentImageIndex]}
                  alt={`${product.brandName} ${product.modelName} - ${currentImageType} view`}
                  style={styles.mainImage}
                  onClick={() => openImageModal(currentImageIndex)}
                  onError={(e) => {
                    if (!imageError[currentImageType + currentImageIndex]) {
                      handleImageError(currentImageIndex);
                      e.target.src = "carousel/carslider.png"; // Fallback image
                    }
                  }}
                />

                {/* Navigation buttons and counter remain the same */}
                <IconButton
                  sx={{ ...styles.navButton, ...styles.leftButton }}
                  onClick={handlePrevImage}
                  aria-label="previous image"
                >
                  <ArrowBackIos />
                </IconButton>

                <IconButton
                  sx={{ ...styles.navButton, ...styles.rightButton }}
                  onClick={handleNextImage}
                  aria-label="next image"
                >
                  <ArrowForwardIos />
                </IconButton>

                <Box sx={styles.imageCounter}>
                  {currentImageIndex + 1} / {currentImages.length}
                </Box>
              </Box>

              {/* Thumbnail grid for all images */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))',
                gap: '8px',
                marginTop: '16px'
              }}>
                {currentImages.map((img, index) => (
                  <Box
                    key={index}
                    sx={{
                      borderRadius: '4px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: currentImageIndex === index ? '2px solid #ff5722' : '1px solid #ddd',
                      aspectRatio: '1',
                    }}
                    onClick={() => {
                      setCurrentImageIndex(index);
                    }}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        if (!imageError[currentImageType + index]) {
                          handleImageError(index);
                          e.target.src = "carousel/carslider.png";
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Exterior, Interior, Product Details and Download All Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: { xs: "4px", sm: "8px", md: "16px" },
                  marginBottom: "16px",
                  marginTop: "16px",
                  width: "100%",
                  minHeight: "50px",
                }}
              >
                {/* Exterior Button */}
                <Button
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: "6px",
                    backgroundColor: "blue",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem", lg: "0.875rem" },
                    height: { xs: "36px", sm: "44px", md: "40px" },
                    textTransform: "none",
                    lineHeight: 1.2,
                    px: { xs: 1, sm: 2, md: 2 },
                    whiteSpace: "normal", // ✅ allow wrapping
                    textAlign: "center",
                    "&:hover": { backgroundColor: "darkblue" },
                  }}
                  onClick={() => switchImageType("exterior")}
                >
                  Exterior
                </Button>

                {/* Interior Button */}
                <Button
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: "6px",
                    backgroundColor: "darkturquoise",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem", lg: "0.875rem" },
                    height: { xs: "36px", sm: "44px", md: "40px" },
                    textTransform: "none",
                    lineHeight: 1.2,
                    px: { xs: 1, sm: 2, md: 2 },
                    whiteSpace: "normal",
                    textAlign: "center",
                    "&:hover": { backgroundColor: "darkblue" },
                  }}
                  onClick={() => switchImageType("interior")}
                >
                  Interior
                </Button>

                {/* Product Details Button */}
                {/* <Button
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: "6px",
                    backgroundColor: "slategray",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem", lg: "0.875rem" },
                    height: { xs: "36px", sm: "44px", md: "40px" },
                    textTransform: "none",
                    lineHeight: 1.2,
                    px: { xs: 1, sm: 2, md: 2 },
                    whiteSpace: "normal",
                    textAlign: "center",
                    "&:hover": { backgroundColor: "rgba(97,97,97,0.8)" },
                  }}
                  onClick={() => {
                    setCurrentImageType("details");
                    setCurrentImageIndex(0);
                  }}
                >
                  Product Details
                </Button> */}

                {/* Download All Button */}
                <Button
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: "6px",
                    backgroundColor: "darkblue",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem", lg: "0.875rem" },
                    height: { xs: "36px", sm: "44px", md: "40px" },
                    textTransform: "none",
                    lineHeight: 1.2,
                    px: { xs: 1, sm: 2, md: 2 },
                    whiteSpace: "normal",
                    textAlign: "center",
                    "&:hover": { backgroundColor: "#00008b" },
                  }}
                  onClick={handleDownloadAll}
                >
                  Download All
                </Button>
              </Box>



              {/* Product Wishlist remove modal */}
              <Modal
                open={openConfirmDialog}
                onClose={() => setOpenConfirmDialog(false)}
                aria-labelledby="remove-from-wishlist-dialog"
                aria-describedby="remove-from-wishlist-confirmation"
              >
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 400,
                  bgcolor: 'background.paper',
                  boxShadow: 24,
                  p: 4,
                  borderRadius: 2,
                }}>
                  <Typography id="remove-from-wishlist-dialog" variant="h6" component="h2">
                    Remove from Wishlist
                  </Typography>
                  <Typography id="remove-from-wishlist-confirmation" sx={{ mt: 2 }}>
                    Are you sure you want to remove this item from your wishlist?
                  </Typography>
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={() => setOpenConfirmDialog(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={async () => {
                        try {
                          await dispatch(removeFromWishlist(modelId));
                          setIsFavorite(false);
                          enqueueSnackbar("Removed from wishlist", { variant: "success" });
                          dispatch(fetchWishlist());
                          setOpenConfirmDialog(false);
                        } catch (error) {
                          enqueueSnackbar("Error removing from wishlist", { variant: "error" });
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              </Modal>

              {/* Modal for enlarged view */}
              <Modal open={modalOpen} onClose={() => setModalOpen(false)} sx={styles.imageModal}>
                <Box sx={styles.modalContent}>
                  {/* Modal for enlarged view */}
                  <img
                    src={currentImages[modalImageIndex]}
                    alt={`${product.brandName} ${product.modelName} - ${currentImageType} view enlarged`}
                    style={styles.modalImage}
                    onError={(e) => {
                      if (!imageError[currentImageType + modalImageIndex]) {
                        handleImageError(modalImageIndex);
                        e.target.src = "carousel/carslider.png"; // Fallback image
                      }
                    }}
                  />

                  {/* Navigation buttons */}
                  <IconButton
                    sx={{ ...styles.modalNavButton, ...styles.modalLeftButton }}
                    onClick={handleModalPrevImage}
                    aria-label="previous image"
                  >
                    <ArrowBackIos />
                  </IconButton>

                  <IconButton
                    sx={{ ...styles.modalNavButton, ...styles.modalRightButton }}
                    onClick={handleModalNextImage}
                    aria-label="next image"
                  >
                    <ArrowForwardIos />
                  </IconButton>

                  {/* Image counter */}
                  <Box sx={styles.imageCounter}>
                    {modalImageIndex + 1} / {currentImages.length}
                  </Box>

                  {/* Image type selector buttons - positioned on the image */}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: { xs: "6px", sm: "16px" },
                      width: "100%",
                      mt: 2, // margin-top to create space below image
                    }}
                  // ❌ REMOVED the onClick handler from the container
                  >
                    {[
                      { label: "Exterior", color: "blue", hover: "darkblue", action: () => switchImageType("exterior") },
                      { label: "Interior", color: "darkturquoise", hover: "#009999", action: () => switchImageType("interior") },
                      // { label: "Product Details", color: "slategray", hover: "#5a6978", action: () => { setCurrentImageType("details"); setModalImageIndex(0); } },
                      { label: "Download All", color: "darkblue", hover: "#00008b", action: handleDownloadAll }, // ✅ Only this button triggers download
                    ].map((btn, i) => (
                      <Button
                        key={i}
                        onClick={btn.action} // ✅ Each button gets its own specific action
                        sx={{
                          flex: "1 1 auto",
                          minWidth: { xs: "80px", sm: "120px" },
                          height: { xs: "28px", sm: "40px" },
                          fontSize: { xs: "0.7rem", sm: "0.875rem" },
                          fontWeight: "bold",
                          textTransform: "none",
                          backgroundColor: btn.color,
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: btn.hover,
                          },
                        }}
                      >
                        {btn.label}
                      </Button>
                    ))}
                  </Box>

                </Box>
              </Modal>
            </Box>

            <Box sx={styles.productInfo}>
              {/* <Typography variant="h4" fontWeight="bold">{`${product.brandName}`} {`${product.modelName}`} {`${product.year}`}</Typography> */}
              <Typography variant="h4" fontWeight="bold">
                {product.modelName?.toLowerCase().includes(product.brandName?.toLowerCase())
                  ? `${product.modelName} ${product.year}`
                  : `${product.brandName} ${product.modelName} ${product.year}`}
              </Typography>
              <Box
                sx={{
                  marginTop: "15px",
                  // marginLeft: "15px",
                  bottom: 8,
                  left: 8,
                  display: "flex",
                  // gap: 0.5,
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                {product.ourPick && (
                  <Chip
                    label="Our Pick"
                    size="small"
                    sx={{
                      fontSize: isMobile ? "0.55rem" : "0.65rem",
                      height: isMobile ? "16px" : "26px",
                      color: "#dd006e", // Neon pink
                      backgroundColor: "#ffbddf",
                      fontWeight: "bold",
                      border: "1px solid #FF69B4",
                      "&:hover": { backgroundColor: "white" },
                    }}
                  />
                )}
                {product.carGrade && (
                  <Chip
                    label={`Grade ${product.carGrade || "A"}`}
                    size="small"
                    sx={{
                      fontSize: isMobile ? "0.55rem" : "0.65rem",
                      height: isMobile ? "16px" : "26px",
                      color: "#009966", // Neon mint
                      backgroundColor: "#9fffe7",
                      fontWeight: "bold",
                      border: "1px solid #00FFAA",
                      "&:hover": { backgroundColor: "white" },
                    }}
                  />
                )}
                {product.specialDeal && (
                  <Chip
                    label="Special Offer"
                    size="small"
                    sx={{
                      fontSize: isMobile ? "0.55rem" : "0.65rem",
                      height: isMobile ? "16px" : "26px",
                      backgroundColor: "#d09fff",
                      color: "#6c2aab", // Periwinkle
                      fontWeight: "bold",
                      border: "1px solid #6c2aab",
                      "&:hover": { backgroundColor: "white" },
                    }}
                  />
                )}
              </Box>
              <Typography variant="body1" sx={{ marginTop: '24px' }}>
                {product.categoriesName !== "NO_CATEGORIES_NAME" && `${product.categoriesName} with `}
                {product.carColor} EXTERIOR
              </Typography>
                            <Typography variant="subtitle1" color="text.secondary" sx={{ marginTop: '12px' }}>{`${product.carMileage} km | ${product.carTransmission} | ${product.carDrive} `}</Typography>
              {/* <Typography variant="body1" sx={{ marginTop: '24px' }}>The 2024 BMW X5 is a luxury SUV that combines power, comfort, and advanced tech.</Typography> */}

              <Box sx={styles.priceContainer}>
                <Typography variant="h4" color="error" fontWeight="bold" sx={{ marginTop: '8px' }}>
                  {`$${product.carPrice.toFixed(2)}`}
                </Typography>
              </Box>

              {/* {product?.isOrderPlace && (
                <Chip
                  label="SOLD"
                  sx={{
                    mt: 1,
                    backgroundColor: '#ff3b30',
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '0.85rem',
                  }}
                />
              )} */}

              <Box sx={styles.buttons}>
                {/* <Button
                  variant="contained"
                  sx={{
                    width: "200px",
                    backgroundColor: "#ff3b30",
                    height: "54px",
                    fontFamily:"Helvetica Neue",
                    fontSize: "18px",
                    
                    '&:hover': {
                      backgroundColor: '#a51811ff'
                    }
                  }}
                  // onClick={handleModalOpen}
                  // onClick={() => setOpen(true)}
                  onClick={() => {
                    setOpen(true);
                    // Scroll to the inquiry section after a small delay to ensure it's rendered
                    setTimeout(() => {
                      document.getElementById('inquiry-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    }, 100);
                  }}
                >
                  INQUIRY NOW
                </Button> */}
                <Button
                  variant="contained"
                  disabled={product?.isOrderPlace === true}
                  sx={{
                    width: "200px",
                    backgroundColor: product?.isOrderPlace ? "#ccc" : "#ff3b30",
                    height: "54px",
                    fontFamily: "Helvetica Neue",
                    fontSize: "18px",
                    '&:hover': {
                      backgroundColor: product?.isOrderPlace ? '#ccc' : '#a51811ff'
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#ccc',
                      color: '#888',
                    }
                  }}
                  onClick={() => {
                    setOpen(true);
                    setTimeout(() => {
                      document.getElementById('inquiry-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    }, 100);
                  }}
                >
                  {product?.isOrderPlace ? "SOLD" : "INQUIRY NOW"}
                </Button>

                {/* <InquiryModal
                  open={open}
                  onClose={() => setOpen(false)}
                  product={product}
                  imageUrl1={imageUrl1}
                /> */}

                {/* <Button
                  variant="contained"
                  color="error"
                  sx={{ width: "200px", height: "54px", fontSize: "16px" }}
                  onClick={() => handleAddToCart(modelId)}
                  disabled={loading || isInCart || cartLoading}
                >
                  {!localStorage.getItem("authToken")
                    ? "LOGIN TO ADD"
                    : cartLoading
                      ? "LOADING..."
                      : isInCart
                        ? "ADDED"
                        : "ADD TO CART"}
                </Button> */}

              </Box >

              {product?.isAucnetCarModel && product?.isAucnetCarModelText === "YES" && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                }}>
                  <img src="//cdn.beforward.jp/elements/promotion/202201/icon_promotion16.gif?_=1641880530" alt="3rd Party Seller" style={{ width: "85px", height: "15px", marginBottom: "10px" }} />
                  <Typography variant="body2" sx={{ color: '#663c00', fontStyle: 'italic', mb: 1 }}>
                    * {product.carRegistrationDate || 'This vehicle'} is a registration date in Stock Country.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#663c00', fontStyle: 'italic', mb: 1 }}>
                    * Chassis number: The chassis number provided is supplied by a third-party vendor and may be temporary. It may be changed prior to shipment; the accurate chassis number will be reflected
                    on official documentation. AmarCar has not verified and does not warrant the authenticity of this information.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#663c00', fontStyle: 'italic', mb: 1 }}>
                    * Accessories & equipment: Any accessories, devices, or equipment that are not standard for passenger vehicles will be removed prior to shipment.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#663c00', fontStyle: 'italic' }}>
                    Import regulations: Buyers must consult and comply with the import regulations of their destination country.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#663c00', fontStyle: 'italic' }}>
                    Dimensions, cargo volume (m³) & weight: The actual exterior dimensions, cargo volume (measured in cubic metres — m³), and vehicle weight may differ from the values provided above.
                  </Typography>
                </Box>
              )}

            </Box >

          </Box >

          {/* Car Specifications Table */}
          < Box sx={styles.specsTable} >
            < Typography
              variant="h3"
              sx={{
                fontWeight: "600",
                fontSize: { xs: "28px", sm: "32px", md: "40px" },
                mb: 2,
                textAlign: "center",
              }}
            >
              <span style={{ color: "#eb2329" }}>Car</span>
              <span style={{ color: "#000000", marginLeft: "10px" }}>Specifications</span>
            </Typography >
            {/* Red underline */}
            < Box
              sx={{
                width: "60px", // adjust width as per taste
                height: "3px",
                backgroundColor: "#eb2329",
                borderRadius: "2px",
                mx: "auto",
                mb: 4
              }}
            />
            < TableContainer component={Paper} elevation={3}
              sx={{
                ...styles.tableContainer,
                overflowX: "auto",   // enables horizontal scroll
                whiteSpace: "nowrap" // prevents cells from breaking
              }}
            >
              <Table sx={{ borderCollapse: 'collapse', width: '100%' }}>
                <TableBody>
                  {/* Row 1 */}
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', width: '16.67%', padding: '16px' }}>
                      Brand
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', width: '16.67%', padding: '16px' }}>
                      {product?.brandName || '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', width: '16.67%', padding: '16px' }}>
                      Drive Type
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', width: '16.67%', padding: '16px' }}>
                      {product?.carDrive || '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', width: '16.67%', padding: '16px' }}>
                      Transmission
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', width: '16.67%', padding: '16px' }}>
                      {product?.carTransmission || '--'}
                    </TableCell>
                  </TableRow>

                  {/* Row 2 */}
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Year
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carRegistrationDate || '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Model
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.modelName || '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Category
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.categoriesName || '--'}
                    </TableCell>
                  </TableRow>

                  {/* Row 3 */}
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Reference Number
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carRefNumber || '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Car Dimensions (m)
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carLength || '--'} x {product?.carWidth || '--'} x {product?.carHeight || '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Engine CC / Motor kW
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carEngineCC ? `${product.carEngineCC} cc` : '--'}
                    </TableCell>
                  </TableRow>

                  {/* Row 4 */}
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Mileage
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carMileage ? `${product.carMileage} km` : '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Fuel Type
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carFuel || '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Steering
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carSteering || '--'}
                    </TableCell>
                  </TableRow>

                  {/* Row 5 */}
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Color
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carColor || '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Seats
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carSeat || '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Doors
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carDoor || '--'}
                    </TableCell>
                  </TableRow>

                  {/* Row 6 */}
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Chassis Number
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carChassisNumber || '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Recycle Fee Paid
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.isRecycleAmountPaidText || '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Model Code
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carModelCode || '--'}
                    </TableCell>
                  </TableRow>

                  {/* Row 7 */}
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Location
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carLocation || '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Capacity
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.carWeight ? `${product.carWeight}` : '--'}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: '600', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      Import Type
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0', padding: '16px' }}>
                      {product?.importType || '--'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer >
          </Box >

          {/* Car Accessories Table */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: "600",
              fontSize: { xs: "28px", sm: "32px", md: "40px" },
              mb: 2,
              textAlign: "center",
              mt: 8 // Add some top margin
            }}
          >
            <span style={{ color: "#eb2329" }}>Car</span>
            <span style={{ color: "#000000", marginLeft: "10px" }}>Accessories</span>
          </Typography>
          {/* Red underline */}
          <Box
            sx={{
              width: "60px",
              height: "3px",
              backgroundColor: "#eb2329",
              borderRadius: "2px",
              mx: "auto",
              mb: 4
            }}
          />

          {/* Check if we have accessories to display */}
          {(accessoriesData.moreAccessories.length > 0 || accessoriesData.filterAccessories.length > 0) ? (
            <TableContainer component={Paper} elevation={3} sx={{
              overflowX: 'auto',
              mb: 8 // Add bottom margin
            }}>
              <Table size="small" sx={{ minWidth: 600 }}>
                <TableBody>
                  {[
                    // Combine all accessories with safe array access
                    ...(Array.isArray(accessoriesData.filterAccessories) ? accessoriesData.filterAccessories : []).map(a => ({
                      id: `f-${a.carFilterAccessoriesId || a.id || Math.random()}`,
                      name: a.carFilterAccessories || a.name || 'Unknown',
                      available: product?.filterAccessories?.some(item =>
                        item.carFilterAccessoriesId === a.carFilterAccessoriesId ||
                        item === a.carFilterAccessories ||
                        item === a.name
                      ) || false
                    })),
                    ...(Array.isArray(accessoriesData.moreAccessories) ? accessoriesData.moreAccessories : []).map(a => ({
                      id: `m-${a.carMoreAccessoriesId || a.id || Math.random()}`,
                      name: a.carMoreAccessories || a.name || 'Unknown',
                      available: product?.moreAccessories?.some(item =>
                        item.carMoreAccessoriesId === a.carMoreAccessoriesId ||
                        item === a.carMoreAccessories ||
                        item === a.name
                      ) || false
                    }))
                  ]
                    .filter(acc => acc.name !== 'Unknown') // Filter out unknown items
                    .sort((a, b) => b.available - a.available) // Sort by availability
                    .reduce((rows, item, index, all) => {
                      if (index % 6 === 0) rows.push(all.slice(index, index + 6));
                      return rows;
                    }, [])
                    .map((row, i) => (
                      <TableRow key={`row-${i}`}>
                        {row.map((acc, cellIndex) => (
                          <TableCell
                            key={acc.id}
                            sx={{
                              borderRight: '1px solid rgba(224, 224, 224, 1)',
                              padding: '16px 10px',
                              width: '16.666%',
                              position: 'relative',
                              '&:last-child': {
                                borderRight: 'none'
                              }
                            }}
                          >
                            {acc.available ? (
                              <Box sx={{
                                display: 'inline-block',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                bgcolor: '#daf9e6',
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                border: '1px solid #4caf50'
                              }}>
                                {acc.name}
                              </Box>
                            ) : (
                              <Typography sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#b3b3b3',
                                whiteSpace: 'nowrap',
                                textDecoration: 'line-through'
                              }}>
                                {acc.name}
                              </Typography>
                            )}
                          </TableCell>
                        ))}
                        {/* Fill remaining columns */}
                        {Array.from({ length: 6 - row.length }).map((_, idx) => (
                          <TableCell
                            key={`empty-${i}-${idx}`}
                            sx={{
                              borderRight: idx < 5 - row.length ? '1px solid rgba(224, 224, 224, 1)' : 'none',
                              width: '16.666%'
                            }}
                          />
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{
              textAlign: 'center',
              py: 4,
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              mb: 8
            }}>
              <Typography variant="body1" color="text.secondary">
                No accessories information available
              </Typography>
            </Box>
          )}

          {/* Inquiry Now Section */}
          {/* {open && ( */}
          {/* Inquiry Now Section */}
            {product?.isOrderPlace ? (
              <Box id="inquiry-section" sx={{ mt: 8, mb: 8, textAlign: 'center' }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "600",
                    fontSize: { xs: "28px", sm: "32px", md: "40px" },
                    mb: 2,
                    textAlign: "center",
                  }}
                >
                  <span style={{ color: "#eb2329" }}>Inquiry</span>
                  <span style={{ color: "#000000", marginLeft: "10px" }}>Now</span>
                </Typography>
                <Box
                  sx={{
                    width: "60px",
                    height: "3px",
                    backgroundColor: "#eb2329",
                    borderRadius: "2px",
                    mx: "auto",
                    mb: 4,
                  }}
                />
                <Box
                  sx={{
                    p: 4,
                    backgroundColor: '#f5f5f5',
                    borderRadius: '12px',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    This car has been sold. Inquiry is no longer available.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box id="inquiry-section" sx={{ mt: 8, mb: 8 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "600",
                    fontSize: { xs: "28px", sm: "32px", md: "40px" },
                    mb: 2,
                    textAlign: "center",
                  }}
                >
                  <span style={{ color: "#eb2329" }}>Inquiry</span>
                  <span style={{ color: "#000000", marginLeft: "10px" }}>Now</span>
                </Typography>
                <Box
                  sx={{
                    width: "60px",
                    height: "3px",
                    backgroundColor: "#eb2329",
                    borderRadius: "2px",
                    mx: "auto",
                    mb: 4,
                  }}
                />
                <ProductInquiry
                  product={product}
                  imageUrl1={imageUrl1}
                  embedded={true}
                  onClose={() => setOpen(false)}
                />
              </Box>
            )}
          {/* )} */}
          <ProductPurchaseFlow />
          {/* Tabs Section */}
          
        </Box >

        <AffordableCarSection />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container >
      <FooterManual />
    </>
  );
};

export default ProductPage;